import PropTypes from "prop-types";
import { DetailedViewWithPagination } from "../shared/DetailedViewWithPagination";
import { Modal } from "@/components/ui/modal";
import { Popconfirm, Table, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Markdown from "react-markdown";
import { markdownStyles } from "../../../../../components/Markdown/markdownStyle";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useReferenceData } from "../../../../../hooks/useReferenceData";
import { TablePagination } from "../../../../../components/pagination/TablePagination";
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { Button as AntdButton } from "antd";
import TableToolbar from "./component/tableToolbar";
import { convertToVNTime } from "../../../../../utils/Date";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

const ManageReferenceData = ({
  tableProps = {},
  className = "",
}) => {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [currentPage, setCurrentPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createData, setCreateData] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 5,
    name: "",
    data: "",
  });
  const [searchDraft, setSearchDraft] = useState({
    page: 1,
    limit: 5,
    name: "",
    data: "",
  });

  // State để lưu trữ dữ liệu hiển thị
  const [localReferenceData, setLocalReferenceData] = useState({
    result: [],
    total: 0,
  });

  const {
    referenceData,
    referenceDataLoading,
    deleteReferenceByIdAsync,
    importReferenceDataCsvAsync,
    createReferenceDataAsync,
    updateReferenceDataAsync,
  } = useReferenceData(searchParams);
  // Cập nhật local state khi API data thay đổi
  useEffect(() => {
    if (referenceData) {
      setLocalReferenceData(referenceData);
    }
  }, [referenceData]);

  // Đồng bộ page state với searchParams.page
  useEffect(() => {
    setPage(searchParams.page);
  }, [searchParams.page]);

  useEffect(() => {
    if (
      !referenceDataLoading &&
      localReferenceData?.result?.length === 0 &&
      searchParams.page > 1
    ) {
      setSearchParams((prev) => ({
        ...prev,
        page: 1,
      }));
      const invalidateQueries = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [
            "reference-data",
            searchParams?.page,
            searchParams?.limit,
            searchParams?.name,
            searchParams?.data,
          ],
        });
      }, 2000);
      setPage(searchParams?.page);
      return () => clearTimeout(invalidateQueries);
    }
  }, [
    localReferenceData?.result?.length,
    referenceDataLoading,
    searchParams.page,
  ]);

  const fileInputRef = useRef();

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: newPage,
      limit: limit,
    }));
  };

  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: 1,
      limit: newLimit,
    }));
  };

  const handleImportClick = () => {
    if (fileInputRef.current) fileInputRef.current.value = null;
    fileInputRef.current?.click();
  };


  // Hàm parse toàn bộ nội dung CSV để xử lý đúng dữ liệu nhiều dòng
  const parseCSVContent = (text) => {
    const records = [];
    let currentRecord = [];
    let currentField = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < text.length) {
      const char = text[i];
      
      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          // Escaped quote (double quotes)
          currentField += '"';
          i += 2; // Skip both quotes
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        currentRecord.push(currentField);
        currentField = '';
        i++;
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // End of record
        currentRecord.push(currentField);
        if (currentRecord.length > 0) {
          records.push(currentRecord);
        }
        currentRecord = [];
        currentField = '';
        // Skip \r\n combination
        if (char === '\r' && text[i + 1] === '\n') {
          i += 2;
        } else {
          i++;
        }
      } else {
        // Giữ nguyên mọi ký tự khác (bao gồm \n, \r trong quotes)
        currentField += char;
        i++;
      }
    }
    
    // Add the last field and record
    if (currentField !== '' || currentRecord.length > 0) {
      currentRecord.push(currentField);
      if (currentRecord.length > 0) {
        records.push(currentRecord);
      }
    }
    
    return records;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Validate định dạng file CSV
    const isCsv =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
    if (!isCsv) {
      toast.error(intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.INVALID_FILE" }));
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      
      // Xử lý CSV đúng cách - parse toàn bộ file thành các record
      const records = parseCSVContent(text);
      if (records.length === 0) {
        toast.error(intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.EMPTY_FILE" }));
        return;
      }
      
      // Kiểm tra header
      const headers = records[0];
      if (headers[0].trim() !== "name" || headers[1].trim() !== "data") {
        toast.error(
          intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.INVALID_HEADERS" })
        );
        return;
      }
      
      // Lọc các record hợp lệ (có giá trị ở cả 2 cột đầu)
      const validRecords = [records[0]];
      for (let i = 1; i < records.length; i++) {
        const record = records[i];
        if (
          record[0] &&
          record[1] &&
          record[0].trim() !== "" &&
          record[1].trim() !== ""
        ) {
          validRecords.push(record);
        }
      }
      if (validRecords.length === 1) {
        toast.error(
          intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.INVALID_DATA" })
        );
        return;
      }
      
      // Tạo file CSV mới từ các record hợp lệ
      const newCsv = validRecords.map(record => 
        record.map(field => `"${field.replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      const newFile = new File([newCsv], file.name, { type: file.type });

      // Gọi API để import (ngầm)
      try {
        const response = await importReferenceDataCsvAsync(newFile);
        const importedItems = response?.imported.map((row) => {
          const { id, name, data, created_at } = row;
          return {
            id: id,
            name,
            data,
            created_at: created_at,
          };
        });

        setLocalReferenceData((prev) => {
          const newTotal = (prev.total || 0) + importedItems.length;
          const currentPageSize = prev.result.length;

          // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
          if (currentPageSize >= limit) {
            const lastPage = Math.ceil(newTotal / limit);

            setSearchParams((prevParams) => ({
              ...prevParams,
              page: lastPage,
            }));
            const invalidateQueries = setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "reference-data",
                  searchParams?.page,
                  searchParams?.limit,
                  searchParams?.name,
                  searchParams?.data,
                ],
              });
            }, 2000);
            return () => clearTimeout(invalidateQueries);
          }

          // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
          return {
            ...prev,
            result: [...importedItems, ...prev.result],
            total: newTotal,
          };
        });
      } catch (error) {
        console.error("Lỗi khi import:", error);
      }
    };
    reader.readAsText(file);
  };

  const isMessageLong = (text) => {
    if (!text) return false;

    // Check if text has multiple lines or is longer than 100 characters
    const hasMultipleLines = text.includes("\n");
    const isLongText = text.length > 150;

    return hasMultipleLines || isLongText;
  };

  const handleSearchChange = (e, isNowEmpty = false) => {
    const { name, value } = e.target;
    setSearchDraft((prev) => {
      const nextDraft = { ...prev, [name]: value };
      if (isNowEmpty) {
        setPage(1);
        setSearchParams((prevParams) => ({
          ...prevParams,
          ...nextDraft,
          page: 1,
          limit: limit, // Cập nhật limit từ state hiện tại
        }));
      }
      return nextDraft;
    });
  };

  const handleSearch = () => {
    setSearchParams({
      ...searchParams,
      ...searchDraft,
      page: 1, // Reset về trang đầu tiên khi search
      limit: limit, // Cập nhật limit từ state hiện tại
    });
    setPage(1); // Cập nhật state page để UI đồng bộ
  };

  // Edit state
  const [editingItem, setEditingItem] = useState(null);

  const handleOpenCreateModal = () => {
    setEditingItem(null);
    setCreateModalOpen(true);
    setCreateName("");
    setCreateData("");
    setValidationErrors({});
  };
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setValidationErrors({});
  };
  const handleSubmitCreate = async () => {
    // Reset validation errors
    setValidationErrors({});

    // Validate required fields
    const errors = {};
    if (!createName.trim()) {
      errors.name = intl.formatMessage({
        id: "AGENTS.HOME.REFERENCE_DATA.ERROR.NAME",
      });
    }
    if (!createData.trim()) {
      errors.data = intl.formatMessage({
        id: "AGENTS.HOME.REFERENCE_DATA.ERROR.DATA",
      });
    }

    // If there are validation errors, display them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (editingItem) {
      try {
        await updateReferenceDataAsync({
          id: editingItem.id,
          data: { name: createName, data: createData },
        });
        setLocalReferenceData((prev) => ({
          ...prev,
          result: prev.result.map((it) =>
            it.id === editingItem.id
              ? { ...it, name: createName, data: createData }
              : it
          ),
        }));
        setCreateModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        console.error("Lỗi khi cập nhật:", error);
      }
      return;
    }

    try {
      const response = await createReferenceDataAsync({
        name: createName,
        data: createData,
      });
      const newItem = {
        id: response?.id,
        name: createName,
        data: createData,
        created_at: response?.created_at,
      };

      setLocalReferenceData((prev) => {
        const newTotal = (prev.total || 0) + 1;
        const currentPageSize = prev.result.length;

        // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
        if (currentPageSize >= limit) {
          const lastPage = Math.ceil(newTotal / limit);

          setSearchParams((prevParams) => ({
            ...prevParams,
            page: lastPage,
          }));
          const invalidateQueries = setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [
                "reference-data",
                searchParams?.page,
                searchParams?.limit,
                searchParams?.name,
                searchParams?.data,
              ],
            });
          }, 2000);
          return () => clearTimeout(invalidateQueries);
        }

        // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
        return {
          ...prev,
          result: [newItem, ...prev.result],
          total: newTotal,
        };
      });
      setCreateModalOpen(false);
    } catch (error) {
      console.error("Lỗi khi tạo mới:", error);
    }
  };

  // Xử lý các sự kiện
  const handleEdit = (item) => {
    setEditingItem(item);
    setCreateName(item.name || "");
    setCreateData(item.data || "");
    setCreateModalOpen(true);
  };

  const confirmDelete = async (item) => {
    try {
      await deleteReferenceByIdAsync(item.id);
      setLocalReferenceData((prev) => ({
        ...prev,
        result: prev.result.filter((resultItem) => resultItem.id !== item.id),
        total: (prev.total || 1) - 1,
      }));
    } catch (error) {
      console.error("Lỗi khi xóa:", error);
    }
  };

  const totalPages = localReferenceData?.result?.length;

  const handlePrev = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNext = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  };

  // Cấu hình cột (định nghĩa bên trong component để truy cập handleEdit và handleDelete)
  const columns = [
    {
      key: "name",
      dataIndex: "name",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.NAME" />,
      type: "text",
      width: "200px",
      align: "left",
    },
    {
      key: "data",
      dataIndex: "data",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.DATA_150_WORD" />,
      type: "text",
      width: "700px",
      align: "left",
      render: (text, record) => {
        const isExpanded = expandedRows[record.id] || false;
        const needsExpansion = isMessageLong(text);
        // Xử lý ký tự xuống dòng \n thành <br /> tags
        const processedText = text ? text.replace(/\n/g, "  \n") : "";

        return (
          <div className="text-[14px] w-full flex flex-col transition-all duration-300 ease-in-out">
            <div className="flex items-start">
              <div
                className={`${
                  !isExpanded && needsExpansion ? "line-clamp-1" : ""
                } flex-1 transition-all duration-300 ease-in-out overflow-hidden`}
              >
                <Markdown
                  components={markdownStyles}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {processedText}
                </Markdown>
              </div>
              {needsExpansion && (
                <button
                  onClick={() =>
                    setExpandedRows((prev) => ({
                      ...prev,
                      [record.id]: !isExpanded,
                    }))
                  }
                  title={`${isExpanded ? "Thu nhỏ" : "Phóng to"}`}
                  className="ml-2 flex-shrink-0 hover:text-[#50b553]"
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className={`h-3 w-3 leading-none text-gray-500`}
                  />
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "created_at",
      dataIndex: "created_at",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.DATE_CREATE" />,
      type: "text",
      width: "150px",
      align: "center",
      render: (text) => <p>{convertToVNTime(text)}</p>,
    },
    {
      key: "action",
      dataIndex: "action",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.ACTION" />,
      type: "actions",
      width: "100px",
      align: "center",
      render: (_value, record) => (
        <div className="flex gap-4 justify-center ">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            className="text-blue-500 hover:text-blue-300"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <Popconfirm
            title={<FormattedMessage id="AGENTS.REFERENCE_DATA.DELETE_TASK" />}
            description={
              <FormattedMessage id="AGENTS.REFERENCE_DATA.DELETE_CONFIRM" />
            }
            onConfirm={() => confirmDelete(record)}
            // onCancel={cancelDelete}
            okText={<FormattedMessage id="AGENTS.REFERENCE_DATA.YES" />}
            cancelText={<FormattedMessage id="AGENTS.REFERENCE_DATA.NO" />}
          >
            <button className="text-red-500 hover:text-red-300">
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <button
        className={`px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors text-sm ${className}`}
        onClick={() => setOpen(true)}
      >
        <FormattedMessage id="AGENTS.REFERENCE_DATA.MANAGE" />
      </button>
      {/* Tiện ích data */}
      <DetailedViewWithPagination
        data={localReferenceData?.result}
        loading={referenceDataLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        handleEdit={handleEdit}
        handleDelete={confirmDelete}
        handlePrev={handlePrev}
        handleNext={handleNext}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.MANAGE_TITLE" })}`}
        width="90vw"
        maxWidth="5xl"
        className="max-h-[calc(100dvh-60px)] my-auto"
      >
        <div className="mt-4">
          <div className="flex justify-between flex-wrap gap-3 mb-4 items-center">
            <TableToolbar
              onSearchChange={handleSearchChange}
              onClickSearch={handleSearch}
              onOpenCreateModal={handleOpenCreateModal}
              onClickImport={handleImportClick}
              onFileChange={handleFileChange}
              fileRef={fileInputRef}
              tooltipLink={
                "https://media.chatme.vn/export_temp/temp_reference_data.csv"
              }
              searchOptions={["name", "data"]}
            />
            <div className="flex">
              <TablePagination
                current={page}
                pageSize={limit}
                total={localReferenceData?.total || 0}
                onChange={handlePageChange}
                onShowSizeChange={handlePageSizeChange}
                pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
              />
            </div>
          </div>
          <Table
            dataSource={localReferenceData?.result}
            loading={referenceDataLoading}
            columns={columns}
            rowKey="id"
            scroll={{ x: "max-content" }}
            bordered
            size="large"
            locale={{
              emptyText: (
                <FormattedMessage id="AGENTS.REFERENCE_DATA.NO_DATA" />
              ),
            }}
            className="custom-table"
            pagination={false}
            {...tableProps}
          />
          <div className="mt-4 flex justify-end">
            <TablePagination
              current={page}
              pageSize={limit}
              total={localReferenceData?.total || 0}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
            />
          </div>
        </div>
      </Modal>
      {/* Modal tạo mới */}
      <Modal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        title={
          editingItem ? (
            <FormattedMessage id="AGENTS.REFERENCE_DATA.UPDATE_REFERENCE_DATA" />
          ) : (
            <FormattedMessage id="AGENTS.REFERENCE_DATA.CREATE_NEW" />
          )
        }
        footer={null}
        width={editingItem ? "700px" : "500px"}
        centered
      >
        <div className="flex flex-col gap-4">
          <label htmlFor="name">
            <FormattedMessage id="AGENTS.REFERENCE_DATA.NAME" />:{" "}
          </label>
          <Tooltip
            title={validationErrors.name}
            open={!!validationErrors.name}
            placement="topLeft"
            color="#ff4d4f"
          >
            <input
              placeholder={intl.formatMessage({
                id: "AGENTS.HOME.REFERENCE_DATA_PLACEHOLDER.NAME",
              })}
              value={createName}
              onChange={(e) => {
                setCreateName(e.target.value);
                if (validationErrors.name) {
                  setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.name;
                    return newErrors;
                  });
                }
              }}
              autoFocus
              id="name"
              required
              className={`w-full border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
                validationErrors.name ? "border-red-500" : ""
              }`}
            />
          </Tooltip>
          <label htmlFor="data">
            <FormattedMessage id="AGENTS.REFERENCE_DATA.DATA" />:{" "}
          </label>
          <Tooltip
            title={validationErrors.data}
            open={!!validationErrors.data}
            placement="topLeft"
            color="#ff4d4f"
          >
            <textarea
              placeholder={intl.formatMessage({
                id: "AGENTS.HOME.REFERENCE_DATA_PLACEHOLDER.DATA",
              })}
              value={createData}
              onChange={(e) => {
                setCreateData(e.target.value);
                if (validationErrors.data) {
                  setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.data;
                    return newErrors;
                  });
                }
              }}
              rows={4}
              id="data"
              required
              className={`w-full h-60 border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
                validationErrors.data ? "border-red-500" : ""
              }`}
            />
          </Tooltip>
          <div className="flex justify-end gap-2 mt-2">
            <AntdButton onClick={handleCloseCreateModal}>
              <FormattedMessage id="AGENTS.REFERENCE_DATA.CANCEL" />
            </AntdButton>
            <AntdButton type="primary" onClick={handleSubmitCreate}>
              {editingItem ? (
                <FormattedMessage id="AGENTS.REFERENCE_DATA.UPDATE" />
              ) : (
                <FormattedMessage id="AGENTS.REFERENCE_DATA.SUBMIT" />
              )}
            </AntdButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

ManageReferenceData.propTypes = {
  tableProps: PropTypes.object,
  className: PropTypes.string,
};

export { ManageReferenceData };
